<script setup lang="ts">
import {computed, ref} from "vue";
import {useStore} from "vuex";

const props = defineProps({
  session: {
    type: Object,
    required: true
  }
});

const store = useStore();
const emit = defineEmits(['show-step-widget', 'delete-item']);

const items = computed(() => props.session?.items || []);
const itemIdx = computed(() => store.getters["session/getItemIdx"]);

const showDeleteModal = ref(false);
const itemIdxToDelete = ref<number | null>(null);

const confirmDelete = (idx: number) => {
  itemIdxToDelete.value = idx;
  showDeleteModal.value = true;
};

const onConfirmDelete = () => {
  if (itemIdxToDelete.value !== null) {
    emit('delete-item', itemIdxToDelete.value);
    showDeleteModal.value = false;
    itemIdxToDelete.value = null;
  }
};

const totalSetPrice = computed(() => {
  return items.value.reduce((acc: number, item: any) => acc + (item.price?.total || 0), 0);
});

const getItemType = (item: any) => {
  const part = item.desc?.parts?.find((p: any) => p.parameter === 'item_type');
  return part ? part.value : '';
};
</script>

<template>
  <div class="w-full bg-white border-b border-gray-100 shadow-sm sticky top-0 z-50">
    <div class="max-w-screen-4xl mx-auto flex items-stretch overflow-hidden h-16 lg:h-24">

      <!-- Back to Steps Section -->
      <div
          @click="emit('show-step-widget')"
          class="flex flex-col justify-center px-4 lg:px-8 border-r border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors bg-gray-100 lg:bg-white z-20"
      >
        <div class="flex items-center space-x-2">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
               stroke="currentColor" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round"
                  d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z"/>
          </svg>
          <span class="hidden lg:block text-[10px] font-bold uppercase tracking-wider">{{
              $t('components.itemV2.itemV2ItemWidget.title')
            }}</span>
        </div>
      </div>

      <!-- Items Section -->
      <div class="flex-1 flex items-stretch bg-gray-100 gap-px overflow-x-auto no-scrollbar">

        <!-- Shop the look Entry -->
        <div
            class="flex-1 flex items-center justify-center px-2 lg:px-6 relative group transition-all duration-300 step-container cursor-pointer min-w-[140px] lg:min-w-[200px]"
            @click="store.commit('session/setViewMode', 'set'); store.commit('session/setItemIdx', 0)"
        >
          <div class="flex w-full items-center space-x-2 lg:space-x-4 pl-4">
            <!-- Thumbnail Icon -->
            <div
                class="w-10 h-auto lg:w-14 lg:h-14 aspect-square rounded-full flex items-center justify-center border-2 transition-all duration-300 overflow-hidden bg-white border-gray-200 group-hover:border-gray-300"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                   stroke="currentColor" class="w-4 h-auto lg:w-8 lg:h-8 text-gray-400 group-hover:text-gray-600">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"/>
              </svg>
            </div>

            <!-- Info -->
            <div class="flex flex-col min-w-0 grow">
              <span
                  class="text-[9px] lg:text-[11px] font-bold uppercase tracking-wider truncate text-gray-400 group-hover:text-gray-600"
              >
                {{ $t('components.itemV2.itemV2Main.complete_your_look') }}
              </span>
              <span
                  class="text-[10px] lg:text-[12px] font-medium truncate text-brand-main"
              >
                {{ $n(totalSetPrice, 'currency') }}
              </span>
            </div>
          </div>
        </div>

        <div
            v-for="(item, idx) in items"
            :key="item.item_number || idx"
            class="flex-1 flex items-center justify-center px-2 lg:px-6 relative group transition-all duration-300 step-container cursor-pointer min-w-[140px] lg:min-w-[200px]"
            :class="{'active': itemIdx === idx}"
            @click="store.commit('session/setViewMode', 'item'); store.commit('session/setItemIdx', idx)"
        >
          <div class="flex w-full items-center space-x-2 lg:space-x-4 pl-4">
            <!-- Thumbnail Image -->
            <div
                class="w-12 h-auto lg:w-14 lg:h-14 rounded-full flex items-center justify-center border-2 transition-all duration-300 overflow-hidden bg-white"
                :class="[itemIdx === idx ? 'border-white' : 'border-gray-200 group-hover:border-gray-300']"
            >
              <div v-if="item.image?.filename" class="relative w-full">
                <img v-if="item.image.filename_layer" :src="item.image.filename_layer"
                     class="absolute insert-0 w-full h-full aspect-square object-cover"
                     alt=""/>

                <img :src="item.image.filename" class="w-full h-full object-cover" alt=""/>
              </div>
              <span v-else class="text-xs font-bold">{{ idx + 1 }}</span>
            </div>

            <!-- Info -->
            <div class="flex flex-col min-w-0 grow">
              <span
                  class="text-[9px] lg:text-[11px] font-bold uppercase tracking-wider truncate transition-colors"
                  :class="itemIdx === idx ? 'text-black' : 'text-gray-400 group-hover:text-gray-600'"
              >
                {{ getItemType(item) }}
              </span>
              <span
                  class="text-[10px] lg:text-[12px] font-medium truncate"
                  :class="itemIdx === idx ? 'text-black' : 'text-brand-main'"
              >
                {{ $n(item.price.total, 'currency') }}
              </span>
              <button
                  @click.stop="confirmDelete(idx)"
                  class="text-[9px] lg:text-[10px] text-brand-main hover:text-black mt-1 uppercase font-bold text-left transition-colors"
              >
                {{ $t('components.itemV2.itemV2ItemWidget.remove') }}
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <transition name="fade">
      <div v-if="showDeleteModal"
           class="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm px-4"
           @click="showDeleteModal = false">
        <div class="bg-white p-8 max-w-sm w-full shadow-2xl border border-gray-100" @click.stop>
          <h3 class="text-xl font-ivymode mb-4 text-gray-900">
            {{ $t('components.itemV2.itemV2ItemWidget.remove_confirm_title') }}</h3>
          <p class="text-gray-600 mb-8 font-light italic">{{
              $t('components.itemV2.itemV2ItemWidget.remove_confirm_text')
            }}</p>
          <div class="space-y-3">
            <button
                @click="onConfirmDelete"
                class="block w-full text-white bg-brand-main p-2 text-center hover:bg-white hover:text-brand-main border border-brand-main text-sm font-light transition-colors"
            >
              {{ $t('components.itemV2.itemV2ItemWidget.confirm') }}
            </button>
            <button
                @click="showDeleteModal = false"
                class="block w-full text-brand-main bg-white p-2 text-center hover:bg-brand-gray border border-brand-main text-sm font-light transition-colors"
            >
              {{ $t('components.itemV2.itemV2ItemWidget.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.step-container {
  background-color: white;
  transition: background-color 0.3s;
  clip-path: polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%, 15px 50%);
  margin-left: -15px;
}

.step-container.active {
  background-color: #eaded5;
  z-index: 10;
}

.step-container:first-child {
  margin-left: 0;
  clip-path: polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%);
}

.step-container:last-child {
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 15px 50%);
}

.step-container:hover {
  background-color: #f9f9f9;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>